/**
 * Header — Barra superior con temporizador global y fecha.
 */

import { useEffect, useState } from 'react';
import { Clock, Play, Pause, Square, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useTimerStore, getTotalElapsed } from '@/stores/timerStore';
import { useAuthStore } from '@/stores/authStore';
import { formatDuration, getGreeting } from '@/lib/utils';

import { GoogleDriveButton } from '@/components/layout/GoogleDriveButton';
import { MetaConnectButton } from '@/components/layout/MetaConnectButton';

export function Header() {
  const timer = useTimerStore();
  const { user, logout } = useAuthStore();
  const [, setTick] = useState(0);

  // Tick every second when timer is running
  useEffect(() => {
    if (!timer.isRunning) return;
    const interval = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(interval);
  }, [timer.isRunning]);

  const elapsed = getTotalElapsed(timer);
  const today = new Date().toLocaleDateString('es-AR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b bg-background/80 backdrop-blur-sm px-6">
      {/* Left: Greeting */}
      <div>
        <h2 className="text-lg font-semibold">{getGreeting()} ✨</h2>
        <p className="text-xs text-muted-foreground capitalize">{today}</p>
      </div>

      {/* Right: Controls & Actions */}
      <div className="flex items-center gap-2.5">
        {/* Meta / Instagram Connect Button */}
        <MetaConnectButton />

        {/* Google Drive Status Button */}
        <GoogleDriveButton />

        {/* User Profile & Logout */}
        {user && (
          <div className="flex items-center gap-1.5 pl-2 border-l border-border">
            <div
              className="w-8 h-8 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center font-bold text-xs ring-2 ring-purple-100 shrink-0"
              title={`${user.name} (${user.email})`}
            >
              {user.picture ? (
                <img
                  src={user.picture}
                  alt={user.name}
                  className="w-8 h-8 rounded-full object-cover"
                />
              ) : (
                user.name?.charAt(0) || 'T'
              )}
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={logout}
              className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
              title="Cerrar sesión"
            >
              <LogOut size={15} />
            </Button>
          </div>
        )}

        {/* Timer (if active) */}
        {(timer.isRunning || timer.elapsed > 0) && (
          <div className="flex items-center gap-3 rounded-full border bg-card px-4 py-2 shadow-sm">
            <Clock size={16} className="text-primary animate-pulse" />
            <div className="text-sm">
              <span className="font-medium">{timer.clientName}</span>
              <span className="mx-2 text-muted-foreground">—</span>
              <span className="font-mono font-bold text-primary">
                {formatDuration(elapsed)}
              </span>
            </div>
            <div className="flex gap-1">
              {timer.isRunning ? (
                <Button size="icon" variant="ghost" onClick={timer.pause} className="h-7 w-7">
                  <Pause size={14} />
                </Button>
              ) : (
                <Button size="icon" variant="ghost" onClick={timer.resume} className="h-7 w-7">
                  <Play size={14} />
                </Button>
              )}
              <Button
                size="icon"
                variant="ghost"
                onClick={timer.stop}
                className="h-7 w-7 text-destructive hover:text-destructive"
              >
                <Square size={14} />
              </Button>
            </div>
            {timer.isRunning && (
              <Badge variant="success" className="text-[10px]">
                REC
              </Badge>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
